import React, { useEffect, useState } from "react";
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Container,
  Grid,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useFormik } from "formik";
import * as Yup from "yup";
import axiosInstance from "../../services/axiosInstance";
import apiEndPointsConfig from "../../services/apiEndPoints";
import { useNavigate } from "react-router-dom";
import JusttawkLogo from "../../assets/Justtawk-01.png";

const theme = createTheme({
  palette: {
    primary: { main: "#4f46e5" },
    secondary: { main: "#f59e0b" },
    text: { primary: "#111827", secondary: "#6b7280" },
    background: { default: "#ffffff", paper: "#f3f4f6" },
  },
  shape: { borderRadius: 16 },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans"',
    h1: { fontWeight: 800, letterSpacing: -0.5 },
    h2: { fontWeight: 800 },
    h3: { fontWeight: 800 },
    h4: { fontWeight: 800 },
    button: { textTransform: "none", fontWeight: 700 },
  },
});

const validationSchema = Yup.object({
  fullName: Yup.string().required("Full Name is required"),
  company: Yup.string().required("Company Name is required"),
  phone: Yup.string()
    .matches(/^[0-9+\-()\s]*$/, "Invalid phone number")
    .required("Phone Number is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  consent: Yup.boolean()
    .oneOf([true], "You must agree to the privacy policy")
    .required(),
});

// ✅ Helper to load Facebook Pixel
const initFacebookPixel = () => {
  if (typeof window === "undefined") return;

  if (window.fbq) {
    // Already initialized
    window.fbq("track", "PageView");
    return;
  }

  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js"
  );

  window.fbq("init", "1575575600295019");
  window.fbq("track", "PageView");
};

export default function JustTalkHero() {
  const [step, setStep] = useState("form");
  const [contact, setContact] = useState("");
  const navigate = useNavigate();

  // 🧩 Helper Functions
  const sendNewUserEmail = async (phoneNumber) => {
    if (!phoneNumber) return console.warn("Phone number is required.");
    try {
      const { data } = await axiosInstance.post(
        apiEndPointsConfig.sendNewUserDetailsEmail,
        { phoneNumber }
      );
      if (data?.success) console.log("New user email sent successfully.");
      else console.warn("Failed to send email:", data?.message);
    } catch (err) {
      console.error("Error sending email:", err.message);
    }
  };

  const addUser = async (userData) => {
    try {
      const { data } = await axiosInstance.post(
        apiEndPointsConfig.campaignUser,
        userData
      );
      if (data?.userId && data?.email) {
        localStorage.setItem("userId", data.userId);
        localStorage.setItem("email", data.email);
        return data;
      }
    } catch (err) {
      console.error("Error adding user:", err.message);
    }
  };

  const verifyOtpHandler = async (otp) => {
    const userId = localStorage.getItem("userId");
    const email = localStorage.getItem("email");

    try {
      const { data } = await axiosInstance.post(apiEndPointsConfig.otpVarification, {
        userId,
        otp,
      });

      // ✅ Track conversion on Verify button (OTP success)
      if (window.fbq) {
        window.fbq("track", "Lead"); // or 'CompleteRegistration', etc.
      }

      localStorage.setItem("token", data?.user?.token);
      await sendNewUserEmail(email);
      navigate("/faq-selection");
    } catch (err) {
      console.error("OTP Verification Error:", err.message);
      otpFormik.setFieldError("otp", "Invalid OTP");
    }
  };

  // 🧲 Warn on unload during OTP
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (step === "otp") {
        e.preventDefault();
        e.returnValue =
          "Are you sure you want to leave this page? Your progress may be lost.";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [step]);

  // 📦 Init Facebook Pixel on page load (PageView)
  useEffect(() => {
    initFacebookPixel();
  }, []);

  // 🧠 Formik for User Registration
  const formik = useFormik({
    initialValues: {
      fullName: "",
      company: "",
      phone: "",
      email: "",
      website: "",
      consent: true,
    },
    validationSchema,
    onSubmit: async (values) => {
      const contactInfo = values.email || values.phone;
      setContact(contactInfo);
      await addUser(values);
      setStep("otp");
    },
  });

  // 🔢 OTP Formik
  const otpFormik = useFormik({
    initialValues: { otp: "" },
    validationSchema: Yup.object({
      otp: Yup.string()
        .matches(/^\d{5}$/, "OTP must be 5 digits")
        .required("OTP is required"),
    }),
    onSubmit: ({ otp }) => verifyOtpHandler(otp),
  });

  // ✏️ Input Field Component
  const renderField = (name, placeholder, required = true) => (
    <TextField
      variant="standard"
      placeholder={placeholder + (required ? "*" : "")}
      name={name}
      value={formik.values[name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      fullWidth
      required={required}
      error={formik.touched[name] && Boolean(formik.errors[name])}
      helperText={formik.touched[name] && formik.errors[name]}
      InputProps={{
        disableUnderline: true,
        sx: {
          bgcolor: "white",
          borderRadius: "6px",
          pl: 2,
          height: 40,
          display: "flex",
          alignItems: "center",
        },
      }}
    />
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Box
        component="section"
        sx={{
          position: "relative",
          minHeight: { xs: "auto", md: "100vh" },
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          py: { xs: 8, md: 0 },
          bgcolor: "white",
        }}
      >
        {/* Orange Background */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: { xs: "300px", sm: "300px", md: "500px" },
            height: { xs: "350px", sm: "300px", md: "80%" },
            bgcolor: "secondary.main",
            borderTopLeftRadius: { xs: 60, md: 120 },
            zIndex: 0,
          }}
        />

        <Container
          maxWidth="xl"
          sx={{
            position: "relative",
            zIndex: 1,
            px: { xs: 1, sm: 4, md: 10 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              p: {
                xs: 1,
                md: 0,
              },
              mt: {
                xs: -6,
                md: 0,
              },
            }}
          >
            <Box
              component="img"
              src={JusttawkLogo}
              alt="JustTawk Logo"
              sx={{
                width: { xs: 120, sm: 160, md: 180 },
                height: "auto",
                objectFit: "contain",
              }}
            />
          </Box>

          <Grid
            container
            spacing={{ xs: 0, md: 8 }}
            alignItems="center"
            sx={{ minHeight: { md: "100vh" } }}
          >
            {/* Left Section */}
            <Grid item xs={12} md={7} sx={{ px: { xs: 2, md: 0 } }}>
              <Stack spacing={3}>
                <Box>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 30, sm: 44, md: 52 },
                      lineHeight: 1.1,
                      fontWeight: 800,
                    }}
                  >
                    Subscription Based
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 30, sm: 44, md: 52 },
                      lineHeight: 1.1,
                      color: "primary.main",
                      mt: 0.5,
                      fontWeight: 800,
                    }}
                  >
                    Contact Center for UAE
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 30, sm: 44, md: 52 },
                      lineHeight: 1.1,
                      mt: 0.5,
                    }}
                  >
                    Brands &amp; StartUps.
                  </Typography>
                </Box>

                <Divider sx={{ width: 240, borderColor: "divider", my: 1 }} />

                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ maxWidth: 700, fontWeight: 500 }}
                >
                  Handle calls, chats &amp; leads with 24/7 Human Agents —
                  multilingual, secure, and built for scaling businesses.
                </Typography>

                <List dense sx={{ pt: { xs: 0, md: 1 } }}>
                  {[
                    "No Setup Fee",
                    "Instant Activation",
                    "No Lock-In Period",
                  ].map((item) => (
                    <ListItem
                      key={item}
                      disableGutters
                      sx={{ py: { xs: 0, md: 0.5 } }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleRoundedIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="h6"
                            sx={{ color: "primary.main", fontWeight: 700 }}
                          >
                            {item}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>

                <Box sx={{ pt: { xs: 0, md: 2 }, pb: { xs: 2, md: 0 } }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      lineHeight: 1.2,
                      fontSize: { xs: 24, sm: 28, md: 34 },
                    }}
                  >
                    Starting at just{" "}
                    <Box component="span" sx={{ color: "secondary.main" }}>
                      499 AED
                    </Box>{" "}
                    per month.
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Right Section */}
            <Grid
              item
              xs={12}
              md={5}
              sx={{ px: { xs: 2, sm: 6 }, mt: { xs: 2, md: 0 } }}
            >
              <Paper
                elevation={10}
                sx={{
                  overflow: "hidden",
                  borderRadius: 1.5,
                  bgcolor: "#d3d3d3",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  height: { md: 560 },
                }}
              >
                {/* Header */}
                <Box sx={{ bgcolor: "white", textAlign: "center", py: 3 }}>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: "#4b40d7" }}
                  >
                    {step === "form" ? "Get Started Now" : "Enter Your OTP"}
                  </Typography>
                </Box>

                {step === "form" ? (
                  // ➕ User Registration Form
                  <Box
                    component="form"
                    onSubmit={formik.handleSubmit}
                    sx={{
                      p: { xs: 3, sm: 4 },
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      height: { xs: "auto", sm: 500, md: 500 },
                    }}
                  >
                    <Stack
                      spacing={2}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        flexGrow: 1,
                        overflowY: "auto",
                      }}
                    >
                      {renderField("fullName", "Full Name")}
                      {renderField("company", "Company Name")}
                      {renderField("phone", "Phone Number")}
                      {renderField("email", "Email")}
                      {renderField("website", "Website (optional)", false)}
                    </Stack>

                    <Box sx={{ px: { xs: 3, sm: 2 }, pb: { xs: 3, sm: 2 } }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="consent"
                            checked={formik.values.consent}
                            onChange={formik.handleChange}
                            color="primary"
                          />
                        }
                        label={
                          <Typography
                            color="black"
                            textAlign="center"
                            sx={{ fontSize: 11 }}
                          >
                            By submitting your details, you consent to receive
                            calls, emails, and messages from Justttawk.
                          </Typography>
                        }
                        sx={{ alignItems: "flex-start", mt: 1 }}
                      />

                      <Box textAlign="center" sx={{ pt: 3, pb: 1, p: 2 }}>
                        <Button
                          type="submit"
                          size="medium"
                          variant="contained"
                          disabled={!formik.values.consent}
                          sx={{
                            px: {
                              xs: 4,
                              md: 7,
                            },
                            py: { xs: 0.8, md: 0.7 },
                            borderRadius: 999,
                            fontWeight: 700,
                            fontSize: { xs: 16, sm: 15 },
                            bgcolor: formik.values.consent
                              ? "#4b40d7"
                              : "#a1a1aa",
                            "&:hover": {
                              bgcolor: formik.values.consent
                                ? "#3a34b5"
                                : "#a1a1aa",
                            },
                          }}
                        >
                          GET A DEMO
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  // 🔢 OTP Step
                  <Box
                    component="form"
                    onSubmit={otpFormik.handleSubmit}
                    sx={{
                      p: { xs: 3, sm: 4 },
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Typography sx={{ mb: 2, color: "#4f46e5" }}>
                      OTP sent to <strong>{contact}</strong>
                    </Typography>

                    <TextField
                      variant="outlined"
                      placeholder="Enter OTP"
                      name="otp"
                      value={otpFormik.values.otp}
                      onChange={otpFormik.handleChange}
                      onBlur={otpFormik.handleBlur}
                      sx={{
                        width: "100%",
                        bgcolor: "white",
                        borderRadius: 1,
                      }}
                    />
                    <div
                      style={{
                        color: "red",
                        marginTop: "4px",
                        marginLeft: "10px",
                        marginBottom: "25px",
                      }}
                    >
                      {otpFormik.touched.otp && otpFormik.errors.otp}
                    </div>

                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      sx={{ borderRadius: 2, width: "100%" }}
                    >
                      Verify
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
